import React, { useState, useEffect } from 'react'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import moment from 'moment-timezone'
import SandboxLeadModal from 'components/tester/SandboxLeadModal'
import SandboxAPI from 'config/api/SandboxAPI'
import { onError } from 'config/lib/errorLib'
import { getLeadDisplayName } from 'config/helpers/Helpers'

// Plain MUI v5 defaults, isolated from the app's Argon (v4) theme.
const theme = createTheme()

const SITES = [
    { value: 'fs', label: 'Financial services (CI, CLI, NPH...)' },
    { value: 'ee', label: 'Energy Efficiency' },
    { value: 'sf', label: 'Solar Finder' }
]

export default function SandboxLeads() {
    const api = new SandboxAPI()
    const today = moment().format('YYYY-MM-DD')

    const [site, setSite] = useState('fs')
    const [fromDate, setFromDate] = useState(today)
    const [toDate, setToDate] = useState(today)
    const [product, setProduct] = useState('all')
    const [leads, setLeads] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [selectedLead, setSelectedLead] = useState(null)

    useEffect(() => {
        getLeads()
    }, [])

    async function getLeads() {
        setLoading(true)
        setError(null)
        try {
            const response = await api.listLeads(site, fromDate, toDate)
            setLeads(response.leads || [])
            setProduct('all')
        } catch(e) {
            setError(e?.response?.data?.error || e.message)
            setLeads([])
            onError(e)
        }
        setLoading(false)
    }

    const products = [...new Set(leads.map((lead) => lead.product))].sort()
    const showLeads = product === 'all' ? leads : leads.filter((lead) => lead.product === product)

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ p: 3 }}>
                <Paper variant='outlined' sx={{ p: 3 }}>
                    <Typography variant='h6'>Dev sandbox leads</Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                        Leads submitted to the lead-service dev API. They are never allocated, emailed or sent to any buyer, and are deleted 30 days after submission.
                    </Typography>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
                        <TextField select size='small' label='Site' value={site} onChange={(e) => setSite(e.target.value)} sx={{ minWidth: 260 }}>
                            {SITES.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField type='date' size='small' label='From' value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                        <TextField type='date' size='small' label='To' value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                        <TextField select size='small' label='Product' value={product} onChange={(e) => setProduct(e.target.value)} sx={{ minWidth: 200 }}>
                            <MenuItem value='all'>All products</MenuItem>
                            {products.map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </TextField>
                        <Button variant='contained' onClick={() => getLeads()} disabled={loading}>
                            Search
                        </Button>
                    </Stack>

                    {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size='small'>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Submitted</TableCell>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Source</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Phone</TableCell>
                                        <TableCell>Valid</TableCell>
                                        <TableCell>Reason</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {showLeads.length === 0 &&
                                        <TableRow>
                                            <TableCell colSpan={8} align='center' sx={{ color: 'text.secondary', py: 4 }}>
                                                No leads for this period
                                            </TableCell>
                                        </TableRow>
                                    }
                                    {showLeads.map((lead) => (
                                        <TableRow key={lead.leadId} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedLead(lead)}>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{lead.createdAt ? moment(lead.createdAt).format('DD/MM/YY HH:mm:ss') : '-'}</TableCell>
                                            <TableCell>{lead.product}</TableCell>
                                            <TableCell>{lead.source}</TableCell>
                                            <TableCell>{getLeadDisplayName(lead.firstName, lead.lastName)}</TableCell>
                                            <TableCell>{lead.email}</TableCell>
                                            <TableCell>{lead.phone}</TableCell>
                                            <TableCell>
                                                {lead.isValid === undefined ? '-' : (
                                                    <Chip size='small' variant='outlined' label={lead.isValid ? 'Yes' : 'No'} color={lead.isValid ? 'success' : 'default'} />
                                                )}
                                            </TableCell>
                                            <TableCell>{lead.invalidReason || ''}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </Box>

            {selectedLead &&
                <SandboxLeadModal
                    isOpen={Boolean(selectedLead)}
                    lead={selectedLead}
                    close={() => setSelectedLead(null)}
                />
            }
        </ThemeProvider>
    )
}
